import { useEffect, useRef } from "react";
import TemplateRenderer from "./TemplateRenderer";

export interface SiteSpec {
  template_id: string; name: string; desc: string; product: string; city: string;
  customizations: Record<string, string>;
  style?: { primary?: string; font?: string; scale?: number };
}

export const POLICES: Record<string, string> = {
  Inter: "Inter, sans-serif", "Playfair Display": "'Playfair Display', serif", Poppins: "Poppins, sans-serif", Montserrat: "Montserrat, sans-serif",
  "Roboto Slab": "'Roboto Slab', serif", "Cormorant Garamond": "'Cormorant Garamond', serif", Georgia: "Georgia, serif", "DM Sans": "'DM Sans', sans-serif",
};

/** Rend un site à partir de sa spécification : modèle, textes, police, couleur et taille des textes (appliquées partout, de façon fiable). */
export default function AtelierCanvas({ spec }: { spec: SiteSpec }) {
  const ref = useRef<HTMLDivElement>(null);
  const scale = spec.style?.scale ?? 1;
  const font = spec.style?.font ? POLICES[spec.style.font] : "";

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    let timer = 0;
    const apply = () => {
      root.querySelectorAll<HTMLElement>("h1,h2,h3,h4,h5,h6,p,li,a,button,span,label,div").forEach((el) => {
        const hasText = Array.from(el.childNodes).some((n) => n.nodeType === 3 && (n.textContent || "").trim());
        if (!hasText) return;
        if (el.dataset.fs0 === undefined) {
          el.dataset.fsInline = el.style.fontSize;
          el.dataset.fs0 = String(parseFloat(getComputedStyle(el).fontSize));
        }
        if (scale === 1) { if (el.dataset.fsInline) el.style.fontSize = el.dataset.fsInline; else el.style.removeProperty("font-size"); }
        else el.style.setProperty("font-size", `${(parseFloat(el.dataset.fs0 as string) * scale).toFixed(2)}px`, "important");
      });
    };
    apply();
    const mo = new MutationObserver(() => { window.clearTimeout(timer); timer = window.setTimeout(apply, 120); });
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    return () => { mo.disconnect(); window.clearTimeout(timer); };
  }, [scale, spec.template_id, spec.name, spec.desc, spec.product, spec.city, spec.customizations]);

  return (
    <div ref={ref} className="atelier-canvas" data-testid="atelier-canvas">
      {font && <style>{`.atelier-canvas, .atelier-canvas * { font-family: ${font} !important; }`}</style>}
      <TemplateRenderer templateId={spec.template_id} name={spec.name} desc={spec.desc} product={spec.product} client="Client" city={spec.city} sector="Général"
        customizations={{ ...spec.customizations, primaryColor: spec.style?.primary || spec.customizations?.primaryColor }} />
    </div>
  );
}
